import axios from 'axios';
import { attachAdminKey } from './adminKey';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});
attachAdminKey(apiClient);

// Tokens API
export const getTokenHolders = async (tokenAddress, limit = 100, offset = 0) => {
  const response = await apiClient.get(`/token/${tokenAddress}/holders`, {
    params: { limit, offset }
  });
  return response.data;
};

export const getTokenTransfers = async (tokenAddress, limit = 50, offset = 0) => {
  const response = await apiClient.get(`/token/${tokenAddress}/transfers`, {
    params: { limit, offset }
  });
  return response.data;
};

export const getTokenMetrics = async (tokenAddress) => {
  // Backend endpoint is /token/{addr}/transfer-metrics (was renamed from /metrics).
  // Keep the helper name `getTokenMetrics` to avoid breaking existing imports.
  const response = await apiClient.get(`/token/${tokenAddress}/transfer-metrics`);
  return response.data;
};

// Etherscan-like Address Overview API (iter 63)
export const getAddressOverview = async (address) => {
  const response = await apiClient.get(`/address/${address}/overview`);
  return response.data;
};

export const getAddressTransactions = async (address, { pageSize = 25, before = null } = {}) => {
  const params = { page_size: pageSize };
  if (before) params.before = before;
  const response = await apiClient.get(`/address/${address}/transactions`, { params });
  return response.data;
};

export const getAddressTokenTransfers = async (address, page = 1, pageSize = 25, opts = {}) => {
  const response = await apiClient.get(`/address/${address}/token-transfers`, {
    params: { page, page_size: pageSize, ...opts }
  });
  return response.data;
};

export const getAddressNativeTransfers = async (address, page = 1, pageSize = 25, opts = {}) => {
  const response = await apiClient.get(`/address/${address}/native-transfers`, {
    params: { page, page_size: pageSize, ...opts }
  });
  return response.data;
};

export const getAddressDexTrades = async (address, page = 1, pageSize = 25, opts = {}) => {
  const response = await apiClient.get(`/address/${address}/dex-trades`, {
    params: { page, page_size: pageSize, ...opts }
  });
  return response.data;
};

export const getAddressPortfolio = async (address) => {
  const response = await apiClient.get(`/address/${address}/portfolio`);
  return response.data;
};

export const getAddressLabel = async (address) => {
  const response = await apiClient.get(`/address/${address}/labels`);
  return response.data;
};

export const upsertAddressLabel = async ({ address, label, tags, category, chain, source = 'web' }) => {
  const response = await apiClient.post(`/address-labels`, { address, label, tags, category, chain, source });
  return response.data;
};

export const deleteAddressLabel = async (address) => {
  const response = await apiClient.delete(`/address-labels/${address}`);
  return response.data;
};

// Watch / Unwatch via the Telegram bot (iter 64)
export const getAddressWatchStatus = async (address) => {
  const response = await apiClient.get(`/address/${address}/watch-status`);
  return response.data;
};

export const watchAddress = async (address, opts = {}) => {
  const response = await apiClient.post(`/address/${address}/watch`, opts);
  return response.data;
};

export const unwatchAddress = async (address) => {
  const response = await apiClient.delete(`/address/${address}/watch`);
  return response.data;
};

// Contracts (Verified Contracts page — iter 64)
export const getVerifiedContracts = async ({ kind = 'all', page = 1, pageSize = 50, chain = null } = {}) => {
  const params = { kind, page, page_size: pageSize };
  if (chain) params.chain = chain;
  const response = await apiClient.get('/contracts', { params });
  return response.data;
};

export const getContractsSummary = async () => {
  const response = await apiClient.get('/contracts/summary');
  return response.data;
};

export const getTokenMetadata = async (address) => {
  const response = await apiClient.get(`/token/${address}/metadata`);
  return response.data;
};

export const getTokenStats = async (address, window = '24h') => {
  const response = await apiClient.get(`/token/${address}/stats`, { params: { window } });
  return response.data;
};

// All Transactions API
export const getAllTransactions = async (limit = 50, offset = 0) => {
  const response = await apiClient.get('/all-transactions', {
    params: { limit, offset }
  });
  return response.data;
};

// Transaction API
export const getTransaction = async (txHash) => {
  const response = await apiClient.get(`/tx/${txHash}`);
  return response.data;
};

// Status API
export const getIndexerStatus = async () => {
  const response = await apiClient.get('/status');
  return response.data;
};

export const getNodeHealth = async () => {
  const response = await apiClient.get('/node-health');
  return response.data;
};

// ============== DEX Endpoints ==============
export const getDexMetrics = async () => {
  const response = await apiClient.get('/dex/metrics');
  return response.data;
};

export const getDexMetricsByPool = async () => {
  const response = await apiClient.get('/dex/metrics/by-pool');
  return response.data;
};

export const getRecentDexSwaps = async (limit = 50, offset = 0) => {
  const response = await apiClient.get('/dex/recent-swaps', { params: { limit, offset } });
  return response.data;
};

// ETH Price endpoint
export const getEthPrice = async () => {
  const response = await apiClient.get('/prices/eth');
  return response.data;
};

// DEX Liquidity endpoint
export const getDexLiquidity = async () => {
  const response = await apiClient.get('/dex/liquidity');
  return response.data;
};

// All-time stats
export const getAllTimeStats = async () => {
  const response = await apiClient.get('/dex/stats/all-time');
  return response.data;
};

// Last 24h stats (rolling window)
export const getLast24hStats = async () => {
  const response = await apiClient.get('/dex/stats/last-24h');
  return response.data;
};

// ATH/ATL stats
export const getAthAtl = async () => {
  const response = await apiClient.get('/dex/stats/ath-atl');
  return response.data;
};

// Day stats (for specific date YYYY-MM-DD)
export const getDayStats = async (date) => {
  const response = await apiClient.get(`/dex/stats/day/${date}`);
  return response.data;
};

// Swaps by day
export const getSwapsByDay = async (date, limit = 100) => {
  const response = await apiClient.get(`/dex/swaps/by-day/${date}`, { params: { limit } });
  return response.data;
};

// Period stats
export const getPeriodStats = async (start, end = null) => {
  const params = { start };
  if (end) params.end = end;
  const response = await apiClient.get('/dex/stats/period', { params });
  return response.data;
};

// Hourly stats (1h, 6h, 12h, 24h)
export const getHourlyStats = async (hours) => {
  const response = await apiClient.get(`/dex/stats/hours/${hours}`);
  return response.data;
};

// Chart data for price/volume
export const getChartData = async (hours = 24) => {
  const response = await apiClient.get(`/dex/chart/${hours}`);
  return response.data;
};

// Chart data for a specific day
export const getDayChartData = async (date) => {
  const response = await apiClient.get(`/dex/chart/day/${date}`);
  return response.data;
};

// Token DEX Metrics
export const getTokenDexMetrics = async (tokenAddress) => {
  const response = await apiClient.get(`/token/${tokenAddress}/dex-metrics`);
  return response.data;
};

// Token Transfer Metrics (On-chain transfers, separate from DEX)
export const getTokenTransferMetrics = async (tokenAddress) => {
  const response = await apiClient.get(`/token/${tokenAddress}/transfer-metrics`);
  return response.data;
};

// ============== Legacy Endpoints ==============
// KROWN holders count endpoint
export const getKrownHoldersCount = async () => {
  try {
    // Try to get real holders first (on-chain verified)
    const response = await apiClient.get('/krown/real-holders');
    return { total_holders: response.data.total_holders || 0 };
  } catch {
    // Fallback to database count
    const response = await apiClient.get('/holders/krown/count');
    return response.data;
  }
};

// Get all KROWN holders
export const getAllHolders = async (limit = 50, offset = 0) => {
  const response = await apiClient.get('/all-holders', { params: { limit, offset } });
  return response.data;
};

// Get whale alerts
export const getWhaleAlerts = async (limit = 50) => {
  const response = await apiClient.get('/whale-alerts', { params: { limit } });
  return response.data;
};

// Export endpoints
export const getExportTransfersUrl = (tokenAddress) => {
  return `${API}/export/transfers/${tokenAddress}`;
};

export const getExportHoldersUrl = (tokenAddress) => {
  return `${API}/export/holders/${tokenAddress}`;
};

// ============== Bot / Share endpoints (iter 69) ==============
export const getBotInfo = async () => {
  const response = await apiClient.get('/bot/info');
  return response.data;
};

// ============== Digest archive (iter 69b) ==============
export const getDigests = async ({ limit = 90 } = {}) => {
  const response = await apiClient.get('/bot/digests', { params: { limit } });
  return response.data;
};

export const getDigest = async (date) => {
  const response = await apiClient.get(`/bot/digests/${date}`);
  return response.data;
};

// ============== Multichain Explorer (iter 84) ==============
export const mcGetChains = async () => {
  const response = await apiClient.get('/mc/chains');
  return response.data;
};

export const mcGetOverview = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/overview`);
  return response.data;
};

export const mcGetTransactions = async (chain, address, params = {}) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/transactions`, { params });
  return response.data;
};

export const mcGetTokenTransfers = async (chain, address, params = {}) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/token-transfers`, { params });
  return response.data;
};

export const mcGetInternal = async (chain, address, params = {}) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/internal`, { params });
  return response.data;
};

export const mcGetPortfolio = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/portfolio`);
  return response.data;
};

export const mcGetDexTrades = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/dex-trades`);
  return response.data;
};

export const mcGetPnl = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/address/${address}/pnl`);
  return response.data;
};

export const mcGetTokenOverview = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/token/${address}/overview`);
  return response.data;
};

export const mcGetTokenHolders = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/token/${address}/holders`);
  return response.data;
};

export const mcGetTokenOhlcv = async (chain, address, tf = '1h', pool = null) => {
  const params = pool ? { tf, pool } : { tf };
  const response = await apiClient.get(`/mc/${chain}/token/${address}/ohlcv`, { params });
  return response.data;
};

export const mcGetNetwork = async (chain) => {
  const response = await apiClient.get(`/mc/${chain}/network`);
  return response.data;
};

export const mcGetTokenAnalytics = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/token/${address}/analytics`);
  return response.data;
};

export const mcGetTokenFirstBuys = async (chain, address, limit = 500) => {
  const response = await apiClient.get(`/mc/${chain}/token/${address}/first-buys`, { params: { limit } });
  return response.data;
};

export const mcGetTokenPools = async (chain, address) => {
  const response = await apiClient.get(`/mc/${chain}/token/${address}/pools`);
  return response.data;
};

export const mcGetPoolReserves = async (chain, pool) => {
  const response = await apiClient.get(`/mc/${chain}/pool-reserves`, { params: { pool } });
  return response.data;
};

export const mcGetTrending = async (chain, limit = 12) => {
  const response = await apiClient.get(`/mc/${chain}/trending`, { params: { limit } });
  return response.data;
};

export const mcGetNewPools = async (chain, limit = 12) => {
  const response = await apiClient.get(`/mc/${chain}/new-pools`, { params: { limit } });
  return response.data;
};

export const bundleGetPoolReserves = async (chain, pool) => {
  const response = await apiClient.get('/bundle-calc/pool-reserves', { params: { chain, pool } });
  return response.data;
};

export default apiClient;
